import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { faArrowRight } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import React from "react";
import { useAnalytics } from "use-analytics";

import { useQuizWriterExampleTopics } from "./decorativeText";
import pencilIcon from "./pencil.svg";

interface QuizWriterStartFormProps {
  topic: string;
  onTopicChange: (topic: string) => void;
  onCreateQuiz: () => void;
  loading: boolean;
}

export function QuizWriterStartForm({
  topic,
  onTopicChange,
  onCreateQuiz,
  loading,
}: QuizWriterStartFormProps) {
  const { t } = useTranslation("learnerApp\\Quiz");
  const { track } = useAnalytics();
  const quizWriterExampleTopics = useQuizWriterExampleTopics(t);
  return (
    <Box
      sx={{
        marginBottom: 4,
        backgroundColor: "common.white",
        borderRadius: "20px",
        boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
        border: "1px solid",
        borderColor: "grey.100",
        padding: { xs: 2.5, sm: 3.5 },
      }}
    >
      <Box flex sx={{ flexDirection: "column", gap: 3 }}>
        <Box
          flex
          sx={{
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: 36,
          }}
        >
          <QuizWriterPencilMark />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "2.25rem",
              textAlign: "center",
              marginBottom: "4px",
            }}
          >
            {t("Quiz Writer")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "1.6rem",
              textAlign: "center",
            }}
          >
            {t("Create a custom 5 question quiz on any topic.")}
          </Typography>
        </Box>
        <Box
          flex
          sx={{
            flexDirection: "column",
            gap: 1.5,
            padding: "0 34px 34px 34px",
          }}
        >
          <Typography
            variant="subtitle2"
            component="label"
            htmlFor="quiz-writer-topic-input"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              fontSize: "1.6rem",
              marginBottom: "6.5px",
            }}
          >
            {t("What should your quiz be about?")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 10,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "stretch" },
            }}
          >
            <Box
              id="quiz-writer-topic-input"
              component="input"
              value={topic}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onTopicChange(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  onCreateQuiz();
                }
              }}
              placeholder={t("Try Dinosaurs, the Roman Empire, or Gravity")}
              disabled={loading}
              aria-label={t("Quiz topic")}
              sx={{
                flex: 1,
                minWidth: 0,
                height: "45px",
                padding: "12px 16px",
                fontSize: "1.6rem",
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "grey.300",
                outline: "none",
                fontFamily: "inherit",
                "&:focus": {
                  borderColor: "primary.main",
                },
                "&:disabled": {
                  backgroundColor: "grey.100",
                },
              }}
            />
            <Button
              onClick={onCreateQuiz}
              disabled={loading || !topic.trim()}
              variant="contained"
              sx={{ fontWeight: 800, fontSize: "1.6rem", padding: "8px 21px" }}
            >
              {loading ? t("Creating...") : t("Create quiz")}{" "}
              <Icon icon={faArrowRight} sx={{ marginLeft: "6px" }} />
            </Button>
          </Box>
          <Box
            flex
            sx={{ flexDirection: "column", gap: 1.5, marginTop: "20px" }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1.5rem",
                color: "#5c5c5c",
                marginBottom: "8.5px",
              }}
            >
              {t("Try one of these")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginTop: 0.5,
              }}
            >
              {quizWriterExampleTopics.map(example => (
                <Button
                  key={example}
                  type="button"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  onClick={() => {
                    track("quiz_writer_example_topic_click", {
                      topic: example,
                    });
                    onTopicChange(example);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "999px",
                    borderColor: "grey.300",
                    color: "text.primary",
                    paddingY: 10,
                    paddingX: 20,
                    "&:hover:not(:disabled), &:focus:not(:disabled)": {
                      borderColor: "grey.400",
                      backgroundColor: "grey.50",
                      color: "text.primary",
                    },
                    "&:focus-visible": {
                      outlineColor: "grey.500",
                    },
                  }}
                >
                  {example}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function QuizWriterPencilMark() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        marginBottom: "17px",
      }}
    >
      <Box
        sx={{
          height: 68,
          width: 68,
          borderRadius: "18px",
          backgroundColor: "#ffdbf0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2.5,
        }}
      >
        <Box
          component="img"
          src={pencilIcon}
          alt=""
          sx={{ width: 36, height: 36, display: "block" }}
        />
      </Box>
    </Box>
  );
}
